/**
 * Braintree's own browser scripts, which Spreedly's `createBraintreePaymentElements` drives but does
 * not load. Pinned to the version Spreedly's Braintree guide documents; `client` goes first because
 * every other component is created from it.
 */
const BRAINTREE_VERSION = '3.85.3';
const BASE_URL = `https://js.braintreegateway.com/web/${BRAINTREE_VERSION}/js`;

const SCRIPTS_BY_WALLET: Record<'paypal' | 'venmo', string[]> = {
  paypal: ['paypal-checkout'],
  venmo: ['venmo', 'data-collector'],
};

const loaded = new Map<string, Promise<void>>();

function loadScript(name: string): Promise<void> {
  const cached = loaded.get(name);

  if (cached) {
    return cached;
  }

  const promise = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = `${BASE_URL}/${name}.min.js`;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      script.remove();
      reject(new Error(`Failed to load Braintree ${name}`));
    };

    document.head.appendChild(script);
  }).catch((error: Error) => {
    // Not cached as a failure: a retry once the network is back must fetch again.
    loaded.delete(name);

    throw error;
  });

  loaded.set(name, promise);

  return promise;
}

export default async function loadBraintreeScripts(wallet: 'paypal' | 'venmo'): Promise<void> {
  await loadScript('client');
  await Promise.all(SCRIPTS_BY_WALLET[wallet].map(loadScript));
}
